import React, { useCallback, useEffect, useMemo } from 'react'
import partition from 'lodash/partition'
import { useDraggableList } from 'transfix-ui/components/DraggableList'
import { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentStopTypes } from 'types/graphqlTypes'
import { useShipmentIdContext } from '../../context/ShipmentIdContext'
import { ShipmentStopList } from './components/ShipmentStopList/ShipmentStopList'
import { useShipmentStopOrderQuery } from './graphql/ShipmentStopOrder'
import { useDeleteStop } from './hooks/useDeleteStop'
import { useRearrangeStops } from './hooks/useRearrangeStops'

const getInlineMessageProps = (errors: string[]) => {
  if (!errors.length) {
    return undefined
  }

  return {
    message: (
      <div>
        {errors.map(error => (
          <div key={error}>{error}</div>
        ))}
      </div>
    ),
    type: INLINE_MESSAGE_VARIANTS.ALERT,
  }
}

interface IRearrangeShipmentStopsModalProps extends Omit<IModalBaseProps, 'modalType' | 'onClose'> {
  onClose: (toastMessage?: string) => void
}

const useRearrangeStopsModalStyles = makeStyles({
  header: {
    borderBottom: '1px solid',
    borderBottomColor: brandColors.coolGray4,
  },
})

export const RearrangeShipmentStopsModal = ({
  onClose,
  open = false,
  ...props
}: IRearrangeShipmentStopsModalProps) => {
  const shipmentId = useShipmentIdContext()

  const {
    rearrangeStops,
    errorMessages: rearrangeErrorMessages = [],
    loading: isRearrangeMutationLoading,
  } = useRearrangeStops()

  const {
    deleteShipmentStop,
    errorMessages: deleteErrorMessages = [],
    loading: isDeleteMutationLoading,
  } = useDeleteStop()

  const { data, loading } = useShipmentStopOrderQuery({
    variables: {
      shipmentId,
    },
  })

  const { shipment } = data || {}
  const { stops: shipmentStops = [] } = shipment || {}

  const [pickupStops, deliveryStops] = useMemo(() => {
    return partition(shipmentStops, ({ stopType }) => {
      return stopType === IShipmentStopTypes.pickup
    })
  }, [shipmentStops])

  const {
    list: pickupList = [],
    dragEndHandler: dragPickupHandler,
    reset: resetPickupStops,
  } = useDraggableList({
    list: pickupStops,
  })

  const {
    list: deliveryList = [],
    dragEndHandler: dragDeliveryHandler,
    reset: resetDeliveryStops,
  } = useDraggableList({
    list: deliveryStops,
  })

  /**
   * Reset list data when async data updates
   */
  useEffect(() => {
    resetPickupStops(pickupStops)
  }, [resetPickupStops, pickupStops])

  /**
   * Reset list data when async data updates
   */
  useEffect(() => {
    resetDeliveryStops(deliveryStops)
  }, [deliveryStops, resetDeliveryStops])

  const saveHandler = useCallback(async () => {
    const isSuccessful = await rearrangeStops(pickupList, deliveryList)
    if (isSuccessful) {
      onClose('Updated stop order successfully')
    }
  }, [rearrangeStops, pickupList, deliveryList, onClose])

  const deleteHandler = useCallback(
    async (shipmentStopId: string) => {
      const isSuccessful = await deleteShipmentStop(shipmentStopId)
      if (isSuccessful) {
        onClose('Removed stop successfully')
      }
    },
    [deleteShipmentStop, onClose]
  )

  const actions: IModalBaseProps['actions'] = useMemo(() => {
    return [
      {
        label: 'Cancel',
        action: () => onClose(),
        type: 'dismiss',
        dataTest: 'cancel-button',
      },
      {
        label: isRearrangeMutationLoading ? 'Loading...' : 'Save',
        action: saveHandler,
        type: 'confirm',
        dataTest: 'confirm-button',
        disabled: isRearrangeMutationLoading,
      },
    ]
  }, [isRearrangeMutationLoading, onClose, saveHandler])

  const isPickupListDisabled = [
    isDeleteMutationLoading,
    isRearrangeMutationLoading,
    loading,
    pickupList.length < 2,
  ].some(Boolean)

  const isDeliveryListDisabled = [
    isDeleteMutationLoading,
    isRearrangeMutationLoading,
    loading,
    deliveryList.length < 2,
  ].some(Boolean)

  const combinedMessages = useMemo(() => [...rearrangeErrorMessages, ...deleteErrorMessages], [
    deleteErrorMessages,
    rearrangeErrorMessages,
  ])

  const inlineMessageProps = useMemo(() => getInlineMessageProps(combinedMessages), [
    combinedMessages,
  ])

  const classes = useRearrangeStopsModalStyles()

  return (
    <ModalBase
      dataTest='RearrangeShipmentStopsModal'
      modalType={MODAL_TYPES.form}
      size={8}
      closeOnBackdropClick
      open={open}
      actions={actions}
      onClose={onClose}
      headerClassName={classes.header}
      inlineMessageProps={inlineMessageProps}
      title='Rearrange Stops'
      {...props}>
      <div data-test='RearrangeShipmentStops'>
        <Box pt={2} pb={2}>
          <Box pb={2}>
            <Typography variant='h3' component='div'>
              Pick up stops
            </Typography>
          </Box>
          <ShipmentStopList
            stops={pickupList}
            droppableId='shipment-stops-pickup'
            onDragEnd={dragPickupHandler}
            loading={loading}
            isDisabled={isPickupListDisabled}
            onDeleteStop={deleteHandler}
          />
        </Box>
        <Box pb={2}>
          <Box pb={2}>
            <Typography variant='h3' component='div'>
              Delivery stops
            </Typography>
          </Box>
          <ShipmentStopList
            stops={deliveryList}
            droppableId='shipment-stops-delivery'
            onDragEnd={dragDeliveryHandler}
            isDisabled={isDeliveryListDisabled}
            onDeleteStop={deleteHandler}
          />
        </Box>
      </div>
    </ModalBase>
  )
}
